import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Mail, RefreshCw, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@design-system/components/button'
import { Text } from '@design-system/components/Text'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { getUserInitials } from '@/lib/utils/avatar'
import { OrgMember, OrgInvite, InviteDisplayStatus } from '@/lib/api'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'

interface TeamMembersTableProps {
  members: Array<OrgMember>
  invites: Array<OrgInvite>
  currentUserId: string
  isAdmin: boolean
  loading?: boolean
  canInvite?: boolean
  onInvite?: () => void
  onResendInvite: (inviteId: number) => Promise<void>
  onRevokeInvite: (inviteId: number) => Promise<void>
  onRemoveMember: (userId: string) => Promise<void>
}

const ROWS_PER_PAGE = 10
const MAX_PAGE_BUTTONS = 7

const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, member: 2 }

const ROLE_BADGE: Record<string, string> = {
  owner: 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  admin: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  member: 'bg-muted text-muted-foreground',
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  pending: 'bg-muted text-muted-foreground',
  expired: 'bg-muted text-muted-foreground',
}

interface UnifiedRow {
  key: string
  kind: 'member' | 'invite'
  name: string | null
  email: string
  role: string
  status: 'active' | 'pending' | 'expired'
  date: string
  sortOrder: number
  memberId?: string
  inviteId?: number
  displayStatus?: InviteDisplayStatus
  avatarSeed?: string | null
}

function buildRows(
  members: Array<OrgMember>,
  invites: Array<OrgInvite>,
): Array<UnifiedRow> {
  const memberRows: Array<UnifiedRow> = members.map((m) => ({
    key: `member-${m.id}`,
    kind: 'member',
    name: m.name,
    email: m.email,
    role: m.orgRole,
    status: 'active' as const,
    date: m.createdAt,
    sortOrder: 0,
    memberId: m.id,
    avatarSeed: m.avatarSeed,
  }))

  const visibleInvites = invites.filter((inv) => inv.displayStatus !== 'revoked')
  const inviteRows: Array<UnifiedRow> = visibleInvites.map((inv) => ({
    key: `invite-${inv.id}`,
    kind: 'invite',
    name: null,
    email: inv.email,
    role: inv.role,
    status: (inv.displayStatus === 'pending' ? 'pending' : 'expired') as 'pending' | 'expired',
    date: inv.createdAt,
    sortOrder: inv.displayStatus === 'pending' ? 1 : 2,
    inviteId: inv.id,
    displayStatus: inv.displayStatus,
  }))

  const all = [...memberRows, ...inviteRows]

  all.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    if (a.sortOrder === 0) {
      const roleA = ROLE_ORDER[a.role] ?? 99
      const roleB = ROLE_ORDER[b.role] ?? 99
      if (roleA !== roleB) return roleA - roleB
      return new Date(a.date).getTime() - new Date(b.date).getTime()
    }
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  return all
}

function formatShortDate(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getPageList(
  currentPage: number,
  totalPages: number,
): Array<number | 'ellipsis'> {
  if (totalPages <= MAX_PAGE_BUTTONS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const pages: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)
  if (start > 2) pages.push('ellipsis')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < totalPages - 1) pages.push('ellipsis')
  pages.push(totalPages)
  return pages
}

function isLastOwner(row: UnifiedRow, allRows: Array<UnifiedRow>): boolean {
  if (row.role !== 'owner') return false
  const ownerCount = allRows.filter(
    (r) => r.kind === 'member' && r.role === 'owner',
  ).length
  return ownerCount <= 1
}

type ConfirmState =
  | { kind: 'remove'; userId: string; displayName: string }
  | { kind: 'revoke'; inviteId: number }
  | null

export function TeamMembersTable(props: TeamMembersTableProps) {
  const {
    members,
    invites,
    currentUserId,
    isAdmin,
    loading,
    canInvite,
    onInvite,
    onResendInvite,
    onRevokeInvite,
    onRemoveMember,
  } = props
  const { t, i18n } = useTranslation('app')
  const dateLocale = intlLocaleForUi(i18n.language)

  const rows = useMemo(() => buildRows(members, invites), [members, invites])

  const [page, setPage] = useState(1)
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [confirmState, setConfirmState] = useState<ConfirmState>(null)

  useEffect(() => {
    setPage(1)
  }, [members, invites])

  const totalRows = rows.length
  const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE))

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const startIdx = (page - 1) * ROWS_PER_PAGE
  const visibleRows = rows.slice(startIdx, startIdx + ROWS_PER_PAGE)
  const rangeStart = totalRows === 0 ? 0 : startIdx + 1
  const rangeEnd = Math.min(totalRows, startIdx + visibleRows.length)

  async function handleResend(inviteId: number) {
    setActionLoadingId(`resend-${inviteId}`)
    try {
      await onResendInvite(inviteId)
      toast.success('Invitation resent')
    } catch {
      toast.error('Failed to resend invitation')
    } finally {
      setActionLoadingId(null)
    }
  }

  function handleRevoke(inviteId: number) {
    setConfirmState({ kind: 'revoke', inviteId })
  }

  function handleRemove(userId: string, name: string | null) {
    setConfirmState({ kind: 'remove', userId, displayName: name || 'this member' })
  }

  async function handleConfirmed() {
    if (!confirmState) return
    const state = confirmState
    setConfirmState(null)

    if (state.kind === 'remove') {
      setActionLoadingId(`remove-${state.userId}`)
      try {
        await onRemoveMember(state.userId)
        toast.success('Member removed')
      } catch {
        toast.error('Failed to remove member')
      } finally {
        setActionLoadingId(null)
      }
    } else {
      setActionLoadingId(`revoke-${state.inviteId}`)
      try {
        await onRevokeInvite(state.inviteId)
        toast.success('Invitation revoked')
      } catch {
        toast.error('Failed to revoke invitation')
      } finally {
        setActionLoadingId(null)
      }
    }
  }

  const colCount = isAdmin ? 5 : 4

  return (
    <div className="flex flex-col gap-4" data-testid="team-members-table">
      <div className="flex items-center justify-between">
        <Text size="sm" weight="medium">
          {t('account.teamMembers')}
        </Text>
        {canInvite && onInvite && (
          <Button
            variant="outline"
            size="sm"
            onClick={onInvite}
            className="h-[30px] rounded-[6px] border-border px-4 text-[14px] font-medium text-foreground hover:bg-muted"
          >
            <UserPlus className="mr-1.5 h-3.5 w-3.5" />
            {t('account.invite')}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto -mx-4 md:mx-0">
        <table className="w-full border-separate border-spacing-0 text-left text-sm min-w-[480px]">
          <thead>
            <tr>
              <th scope="col" className="rounded-l-xl bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground dark:bg-card">
                {t('account.colMember')}
              </th>
              <th scope="col" className="bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground dark:bg-card">
                {t('account.colRole')}
              </th>
              <th scope="col" className="bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground dark:bg-card">
                {t('account.colStatus')}
              </th>
              <th scope="col" className={`bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground dark:bg-card ${!isAdmin ? 'rounded-r-xl' : ''}`}>
                {t('account.colDate')}
              </th>
              {isAdmin && (
                <th scope="col" className="rounded-r-xl bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground text-right dark:bg-card">
                  {t('account.colActions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody data-testid="team-members-tbody">
            {loading && (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-5 py-8 text-center text-sm text-muted-foreground"
                >
                  {t('account.loadingTeam')}
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-5 py-8 text-center text-sm text-muted-foreground"
                >
                  {t('account.noTeam')}
                </td>
              </tr>
            )}
            {!loading &&
              visibleRows.map((row, rowIdx) => {
                const avatarSeed = row.avatarSeed || row.name || row.email || 'user'
                const isSelf = row.kind === 'member' && row.memberId === currentUserId
                const isLast = isLastOwner(row, rows)
                const canRemove = isAdmin && row.kind === 'member' && !isSelf && !isLast
                const canResend =
                  isAdmin && row.kind === 'invite' && (row.status === 'pending' || row.status === 'expired')
                const canRevoke =
                  isAdmin && row.kind === 'invite' && row.status === 'pending'
                const isLastRow = rowIdx === visibleRows.length - 1
                const cellBorder = isLastRow ? '' : 'border-b border-border/40 dark:border-border'

                return (
                  <tr
                    key={row.key}
                    data-testid={`team-row-${row.kind}`}
                  >
                    {/* User column */}
                    <td className={`px-5 py-3.5 ${cellBorder}`}>
                      <div className="flex items-center gap-3">
                        {row.kind === 'member' ? (
                          <UserAvatar
                            initials={getUserInitials(row.name, row.email)}
                            name={row.name || row.email}
                            size="sm"
                          />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          {row.name ? (
                            <>
                              <Text size="sm" weight="medium" className="truncate">
                                {row.name}
                                {isSelf && (
                                  <span className="ml-1.5 text-xs text-muted-foreground">{t('account.you')}</span>
                                )}
                              </Text>
                              <Text size="xs" color="muted" className="truncate">
                                {row.email}
                              </Text>
                            </>
                          ) : (
                            <Text size="sm" weight="medium" className="truncate">
                              {row.email}
                            </Text>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Role column */}
                    <td className={`px-5 py-3.5 text-sm text-muted-foreground ${cellBorder}`}>
                      {row.role === 'owner' || row.role === 'admin' || row.role === 'member'
                        ? t(`account.roles.${row.role}`)
                        : row.role}
                    </td>

                    {/* Status column */}
                    <td className={`px-5 py-3.5 ${cellBorder}`}>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[row.status] || STATUS_BADGE.expired}`}
                      >
                        {row.status === 'active' || row.status === 'pending' || row.status === 'expired'
                          ? t(`account.statuses.${row.status}`)
                          : row.status}
                      </span>
                    </td>

                    {/* Date column */}
                    <td className={`whitespace-nowrap px-5 py-3.5 text-sm text-muted-foreground ${cellBorder}`}>
                      {t(row.kind === 'member' ? 'account.joined' : 'account.invited', {
                        date: formatShortDate(row.date, dateLocale),
                      })}
                    </td>

                    {/* Actions column */}
                    {isAdmin && (
                      <td className={`px-5 py-3.5 text-right ${cellBorder}`}>
                        <div className="flex items-center justify-end gap-2">
                          {canResend && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResend(row.inviteId!)}
                              disabled={actionLoadingId === `resend-${row.inviteId}`}
                              data-testid={`team-resend-${row.inviteId}`}
                              className="h-7 w-7 p-0 text-primary hover:text-primary/70"
                              title={t('account.resendInvite')}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canRevoke && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevoke(row.inviteId!)}
                              disabled={actionLoadingId === `revoke-${row.inviteId}`}
                              data-testid={`team-revoke-${row.inviteId}`}
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive/70"
                              title="Revoke invite"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canRemove && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemove(row.memberId!, row.name)}
                              disabled={actionLoadingId === `remove-${row.memberId}`}
                              data-testid={`team-remove-${row.memberId}`}
                              className="h-7 rounded-md px-2.5 text-xs text-destructive hover:bg-destructive/10"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmState !== null} onOpenChange={(open) => { if (!open) setConfirmState(null) }}>
        <AlertDialogContent
          className="w-[480px] gap-0 rounded-[16px] border-border bg-background p-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)]"
        >
          <div className="flex items-center justify-end p-3">
            <button
              type="button"
              onClick={() => setConfirmState(null)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-7 px-[60px] pb-10">
            <AlertDialogHeader className="items-center gap-3 space-y-0">
              <AlertDialogTitle className="font-display text-2xl font-normal leading-[1.2] tracking-[-0.48px] text-foreground text-center">
                {confirmState?.kind === 'remove'
                  ? `Remove ${confirmState.displayName}?`
                  : 'Revoke this invitation?'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base leading-[1.6] text-muted-foreground text-center">
                {confirmState?.kind === 'remove'
                  ? `${confirmState.displayName} will be removed from the team and lose access to shared resources.`
                  : 'The invitation link will be invalidated and the invitee will no longer be able to join.'}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="flex items-center justify-center gap-4">
              <AlertDialogAction
                variant="destructive"
                onClick={handleConfirmed}
                data-testid="confirm-dialog-confirm"
                className="h-10 rounded-[6px] px-4 text-base font-medium"
              >
                {confirmState?.kind === 'remove' ? 'Yes, remove' : 'Yes, revoke'}
              </AlertDialogAction>
              <AlertDialogCancel
                onClick={() => setConfirmState(null)}
                data-testid="confirm-dialog-cancel"
                className="h-10 rounded-[6px] border-border bg-transparent px-4 text-base font-medium text-foreground hover:bg-muted hover:text-foreground"
              >
                Never mind
              </AlertDialogCancel>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {totalRows > ROWS_PER_PAGE && (
        <div
          className="flex flex-col items-center justify-between gap-2 sm:flex-row"
          data-testid="team-members-pagination"
        >
          <Text size="xs" color="muted">
            Showing {rangeStart}–{rangeEnd} of {totalRows}
          </Text>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              aria-label="Previous page"
              data-testid="team-page-prev"
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            {getPageList(page, totalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span
                  key={`e-${i}`}
                  className="px-2 text-sm text-muted-foreground"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <Button
                  key={p}
                  variant={p === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(p)}
                  disabled={loading}
                  aria-label={`Page ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                  data-testid={`team-page-${p}`}
                  className="h-8 min-w-8 px-2 tabular-nums"
                >
                  {p}
                </Button>
              ),
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages || loading}
              aria-label="Next page"
              data-testid="team-page-next"
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
